import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Percent, Zap, Users } from "lucide-react";

export const Hero = ({ onCompare, onQuote }) => {
  return (
    <section className="relative overflow-hidden grain-bg">
      <div className="hero-blob w-[500px] h-[500px] bg-emerald-200/70 -top-40 -right-40" />
      <div className="hero-blob w-[400px] h-[400px] bg-blue-100 -bottom-40 -left-20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs font-semibold text-slate-700 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Trusted by 50,000+ Indians · Updated daily
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-slate-900 leading-[1.05]">
              Compare Personal Loans<br/>
              from <span className="text-emerald-600">15+ Banks & NBFCs</span><br/>
              in 60 seconds.
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600">
              Loan81 helps you find the <span className="font-semibold text-slate-800">lowest interest rate</span>,
              lowest processing fee, and the loan that actually approves — without paperwork chaos.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-compare-button"
                onClick={onCompare}
                className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-6 rounded-md text-base font-semibold active:scale-95 transition-all"
              >
                Compare Loans Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                data-testid="hero-quote-button"
                onClick={onQuote}
                variant="outline"
                className="border-slate-300 h-12 px-6 rounded-md text-base font-semibold text-slate-800 hover:bg-slate-50 active:scale-95 transition-all"
              >
                Get Free Quote
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <Stat icon={<Percent className="w-4 h-4" />} value="10.40%*" label="Starting rate" />
              <Stat icon={<Zap className="w-4 h-4" />} value="< 24 hrs" label="Disbursal" />
              <Stat icon={<ShieldCheck className="w-4 h-4" />} value="₹50 L" label="Max loan" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <BentoRight />
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ icon, value, label }) => (
  <div>
    <div className="flex items-center gap-1.5 text-emerald-600 mb-1">{icon}</div>
    <div className="font-display font-semibold text-2xl text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mt-0.5">{label}</div>
  </div>
);

const BentoRight = () => (
  <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[440px]">
    <div className="col-span-4 row-span-3 bg-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between overflow-hidden relative">
      <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold">Best Match Today</div>
      <div>
        <div className="text-3xl font-display font-semibold">HDFC Bank</div>
        <div className="text-sm text-slate-300 mt-1">10.50% — 21% p.a.</div>
      </div>
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 rounded-full bg-white/10 border border-white/20">Instant</span>
        <span className="px-2 py-1 rounded-full bg-white/10 border border-white/20">No collateral</span>
      </div>
    </div>
    <div className="col-span-2 row-span-3 bg-emerald-500 text-white rounded-2xl p-5 flex flex-col justify-between">
      <Users className="w-5 h-5" />
      <div>
        <div className="text-3xl font-display font-semibold leading-none">50K+</div>
        <div className="text-xs text-emerald-50 mt-2">Happy borrowers this year</div>
      </div>
    </div>
    <div className="col-span-3 row-span-3 bg-white border border-slate-200 rounded-2xl p-5 flex flex-col justify-between">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Avg Savings</div>
      <div>
        <div className="text-3xl font-display font-semibold text-slate-900">₹42,800</div>
        <div className="text-xs text-slate-500 mt-1">per loan by comparing</div>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full w-4/5 bg-emerald-500" />
      </div>
    </div>
    <div className="col-span-3 row-span-3 bg-slate-100 border border-slate-200 rounded-2xl p-5 flex flex-col justify-between">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Live Rates</div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-700">SBI</span><span className="font-semibold text-slate-900">11.05%</span></div>
        <div className="flex justify-between"><span className="text-slate-700">ICICI</span><span className="font-semibold text-slate-900">10.75%</span></div>
        <div className="flex justify-between"><span className="text-slate-700">Axis</span><span className="font-semibold text-slate-900">10.99%</span></div>
        <div className="flex justify-between"><span className="text-slate-700">Kotak</span><span className="font-semibold text-slate-900">10.99%</span></div>
      </div>
    </div>
  </div>
);
